// src/pages/DownloadsPage.jsx
import React, { useState, useMemo, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import PageBanner from "../components/common/PageBanner";
import DownloadsFilteredSection from "../components/downloads/DownloadsFilteredSection";
import { useLocale } from "../contexts/LocaleContext";
import { getDownloadPageData } from "../api/apiService";
import {
  getManualCategorySlug,
  normalizeManualPathSegment,
} from "../utils/manualCategory";
import {
  flattenStrapiEntity,
  normalizeStrapiRelationArray,
} from "../utils/strapiFlatten";

const DownloadsPage = () => {
  const { locale } = useLocale();
  const { brandSlug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const basePath = location.pathname.startsWith("/manual")
    ? "/manual"
    : "/downloads";

  const [downloadsData, setDownloadsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDownloadsContent = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await getDownloadPageData(locale);
        setDownloadsData(data);
      } catch (err) {
        setError(err.message || "Failed to load content.");
        setDownloadsData(null);
      } finally {
        setLoading(false);
      }
    };
    fetchDownloadsContent();
  }, [locale]);

  const page = useMemo(
    () => flattenStrapiEntity(downloadsData),
    [downloadsData]
  );

  const brands = useMemo(() => {
    const raw = normalizeStrapiRelationArray(page?.cas_download_categories);
    return raw.map((item) => flattenStrapiEntity(item)).filter(Boolean);
  }, [page]);

  useEffect(() => {
    if (loading || !page || brands.length === 0) return;
    if (!brandSlug) {
      const firstSlug = getManualCategorySlug(brands[0]);
      if (firstSlug) {
        navigate(`${basePath}/${encodeURIComponent(firstSlug)}`, {
          replace: true,
        });
      }
    }
  }, [loading, page, brands, brandSlug, navigate, basePath]);

  const selectedBrand = useMemo(() => {
    if (!brandSlug || !brands.length) return null;
    const want = normalizeManualPathSegment(brandSlug);
    return (
      brands.find(
        (b) => normalizeManualPathSegment(getManualCategorySlug(b)) === want
      ) || null
    );
  }, [brandSlug, brands]);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[calc(100vh-200px)]">
        <p className="text-xl text-gray-500">Loading...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto p-4 text-center">
        <p className="text-red-500 text-xl">Error: {error}</p>
      </div>
    );
  }

  if (!downloadsData) {
    return (
      <div className="container mx-auto p-4 text-center">
        <p className="text-gray-700 text-xl">Content not available.</p>
      </div>
    );
  }

  const banner = page?.banner;
  const bannerTitle = banner?.title || "Downloads";
  const bannerImageUrl = banner?.image?.[0]?.url;

  if (brands.length === 0) {
    return (
      <div className="downloads-page min-h-screen">
        <PageBanner
          title={bannerTitle}
          titleColorIsWhite={banner?.titleColorIsWhite ?? true}
          imageUrl={bannerImageUrl}
          bannerHeightClass="h-56 sm:h-64 md:h-72 lg:h-80"
        />
        <div className="container mx-auto px-4 py-16 text-center text-gray-600">
          <p>No brands are configured yet.</p>
        </div>
      </div>
    );
  }

  if (brandSlug && !selectedBrand) {
    const fallback = getManualCategorySlug(brands[0]);
    return (
      <div className="downloads-page min-h-screen">
        <PageBanner
          title={bannerTitle}
          titleColorIsWhite={banner?.titleColorIsWhite ?? true}
          imageUrl={bannerImageUrl}
          bannerHeightClass="h-56 sm:h-64 md:h-72 lg:h-80"
        />
        <div className="container mx-auto px-4 py-16 text-center space-y-4">
          <p className="text-gray-700 text-lg">No downloads found for this path.</p>
          {fallback && (
            <button
              type="button"
              onClick={() =>
                navigate(`${basePath}/${encodeURIComponent(fallback)}`)
              }
              className="text-green-700 font-semibold underline hover:text-green-900"
            >
              Go to downloads
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="downloads-page min-h-screen">
      <PageBanner
        title={bannerTitle}
        titleColorIsWhite={banner?.titleColorIsWhite ?? true}
        imageUrl={bannerImageUrl}
        bannerHeightClass="h-56 sm:h-64 md:h-72 lg:h-80"
      />

      {selectedBrand && (
        <DownloadsFilteredSection
          filterConfig={page?.filter}
          selectedBrand={selectedBrand}
          brands={brands}
          basePath={basePath}
        />
      )}
    </div>
  );
};

export default DownloadsPage;
